<script lang="ts" setup>
import { SlimBanner } from '~/types/Content'
import { getForeground } from '../RichTextPanel/richTextHelpers'

const props = defineProps<SlimBanner>()
const background = props.supportingData?.home?.variant?.children?.backgroundColourForSlimBanner?.value || '#ECFAC5' // Default green onyx
const color =
  props.supportingData?.home?.variant?.children?.backgroundColourForSlimBanner?.children?.fontColour?.value ||
  getForeground(background)
const headingPosition = props.supportingData?.home?.variant?.children?.headingPosition?.value || 'left'
</script>

<template>
  <section :style="{ background, color }" class="content-banner" :class="`content-banner--${headingPosition}`">
    <div class="content-banner__inner-wrapper">
      <Picture
        v-if="picture && headingPosition !== 'above'"
        class="content-banner__media"
        v-bind="picture"
        :preset="'column-panel'"
      />
      <div class="content-banner__container">
        <div class="content-banner__inner-container">
          <h2 v-if="heading" class="content-banner__heading">{{ heading }}</h2>
          <RichText v-if="content" class="content-banner__content" :document="content" />
        </div>
        <Action v-if="link" v-bind="link" />
      </div>
    </div>
  </section>
</template>

<style lang="postcss">
.content-banner {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: var(--space-60) var(--space-70);
  margin-bottom: var(--component-space);

  &.content-banner--left {
    .content-banner__inner-wrapper {
      padding-top: var(--space-70);
      padding-bottom: var(--space-70);
    }

    .content-banner__inner-container {
      max-width: 34rem;
    }
    .content-banner__container {
      align-self: flex-start;
    }
    .content-banner__content {
      max-width: 26rem;
    }

    @media (--tablet) {
      padding: var(--space-60) var(--space-80);

      .content-banner__container {
        display: grid;
        grid-template-columns: 1fr 1fr;
      }

      .content-banner__inner-container {
        grid-column: span 2;
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: var(--space-60);
        align-items: start;
        justify-content: center;
        max-width: none;
      }
      .action-wrapper {
        grid-column-start: 2;
      }
      .content-banner__heading {
        min-width: 17rem;
        max-width: 26rem;
      }
    }
    @media (--desktop) {
      padding: var(--space-100);

      .content-banner__container {
        display: flex;
        gap: var(--space-60);
        flex-direction: row;
        align-items: center;
        justify-content: center;
        align-self: unset;
      }

      .content-banner__inner-container {
        grid-template-columns: 1fr 31.25rem;
        align-items: center;
      }

      .content-banner__heading {
        min-width: 17rem;
        max-width: 34rem;
      }

      .content-banner__content {
        max-width: 26rem;
        margin: auto;
      }

      .action-wrapper {
        padding-top: 0;
      }
    }

    &:has(.content-banner__media) {
      @media (--mobile-only) {
        flex-direction: column;
        padding-top: var(--space-90);
        padding-bottom: var(--space-90);
        .content-banner__inner-wrapper {
          padding-top: 0;
          padding-bottom: 0;
          width: 100%;
        }
      }
      @media (--tablet) {
        .content-banner__inner-wrapper {
          padding-top: var(--space-70);
          padding-bottom: var(--space-70);
          flex-direction: row;
          gap: var(--space-80);
          justify-content: center;
        }
        .content-banner__container,
        .content-banner__inner-container {
          display: flex;
          flex-direction: column;
          align-items: start;
        }
        .content-banner__media {
          max-width: 25rem;
          display: flex;
          justify-content: center;
          align-items: center;
        }
      }
      @media (--desktop) {
        .content-banner__container {
          align-items: center;
          justify-content: center;
          flex-direction: row;
          gap: 3.5rem;
        }
        .content-banner__inner-wrapper {
          padding-top: var(--space-90);
          padding-bottom: var(--space-90);
        }
        .content-banner__content {
          max-width: 26rem;
          margin: 0;
        }
      }
    }
  }

  &.content-banner--above {
    text-align: center;
    @media (--desktop) {
      padding: var(--space-80) var(--space-70);
    }
    .content-banner__inner-wrapper {
      padding-top: var(--space-70);
      padding-bottom: var(--space-70);
      max-width: 37.5rem;
      @media (--desktop) {
        max-width: 45.5rem;
      }
    }
    .content-banner__content {
      max-width: 26rem;
    }
    .content-banner__inner-container {
      display: flex;
      justify-content: center;
      align-items: center;
    }
    .content-banner__container {
      padding-top: var(--space-60);
      padding-bottom: var(--space-60);

      @media (--desktop) {
        padding-top: var(--space-80);
        padding-bottom: var(--space-80);
      }
    }
  }

  .content-banner__container,
  .content-banner__inner-container,
  .content-banner__inner-wrapper {
    display: flex;
    flex-direction: column;
    gap: var(--space-60);
  }
  .content-banner__inner-wrapper {
    max-width: 80rem;
    align-items: center;
  }

  .content-banner__media {
    max-width: 34rem;
    aspect-ratio: 16 / 9;
  }

  &:not(:has(.content-banner__media)) {
    .content-banner__heading {
      font-family: var(--font-display);
      font-size: 1.75rem;
      font-weight: 300;
      text-transform: none;
      @media (--desktop) {
        font-size: 2.25rem;
      }
    }
  }

  .content-banner__heading {
    margin-bottom: 0;
  }

  .content-banner__content {
    & p:last-of-type {
      margin-bottom: 0;
    }
  }
  .action-wrapper {
    padding-top: var(--space-60);
  }
}
</style>
